import { Link, useParams, useNavigate } from "react-router";
import { FiEdit3, FiSave, FiX, FiMapPin, FiSettings, FiUser } from "react-icons/fi";
import { FaServer, FaCube } from "react-icons/fa";
import { BsClock } from "react-icons/bs";
import { HiOutlineBadgeCheck } from "react-icons/hi";
import cn from 'classnames';
import { DesignSystem, getBadgeTailwindClasses } from '../styles/designSystem';
import React, { useEffect } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { useProjectsPage, useProjectsStore } from "../state/projects";
import { useEscalationMatrixPage } from "../state/escalationMatrix";
import { UserInfo } from "../components/UserInfo";
import { useLocationsPage, useLocationsStore } from "../state/locations";
import { toast } from "react-toastify";
import { createRack, createRow, updateRack, updateRoom, updateRow, getRoomTypes } from "../api/locations";
import { NewRowForm } from "../components/NewRowForm";
import { NewRackForm } from "../components/NewRackForm";
// @ts-ignore
import Map, { Marker } from 'react-map-gl/mapbox';
import 'maplibre-gl/dist/maplibre-gl.css';
import { CreateDropdown } from "../components/CreateDropdown";
import { NotificationButton } from "../components/NotificationButton";
import { NewProjectForm } from "../components/NewProjectForm";
import { AddUserModal } from "../components/Project/AddUserModal";
import { AddProjectItemModal } from "../components/Project/AddProjectItemModal";
import { AddActionItemModal } from "../components/Project/AddActionItemModal";
import { AddDailyUpdateModal } from "../components/Project/AddDailyUpdateModal";
import { useProjectUsers } from "../hooks/useProjectUsers";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { Room3DView } from "../components/Room/Room3DView";
import { createProjectItem } from "../api/projects";
import { createProjectDailyUpdates } from "../api/dailyUpdates";
import { createProjectActionItems } from "../api/actionItems";
import { LoadingAnimation } from "../components/LoadingAnimation";

// Function to extract city from site - prioritize Google Address data
const getCityFromSite = (site: any): string => {
  if (!site) return '';
  
  // First, try to use structured Google Address data with new fields
  if (site.google_address_data) {
    const { city, state, locality, administrative_area_level_1 } = site.google_address_data;
    
    // Use locality (city name) from Google Address Data
    if (locality && administrative_area_level_1) {
      return `${locality}, ${administrative_area_level_1}`;
    }
    if (locality) {
      return locality;
    }
    
    // Fallback to old fields for backward compatibility
    if (city && state) {
      return `${city}, ${state}`;
    }
    if (city) {
      return city;
    }
  }
  
  // Fallback to parsing the address string (for backward compatibility)
  const address = site.address;
  if (!address) return '';
  
  // Split address by comma and trim whitespace
  const parts = address.split(',').map((part: string) => part.trim());
  
  if (parts.length >= 2) {
    // Look for the city part (usually the second part, but not always)
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      // Skip if it looks like a state code (2 letters) or zip code (numbers)
      if (!/^\d+$/.test(part) && !/^[A-Z]{2}$/.test(part) && part.length > 2) {
        // Additional check: if this part contains a zip code, extract just the city
        const cityPart = part.split(' ')[0]; // Take first word if it contains zip
        if (cityPart && cityPart.length > 2) {
          return cityPart;
        }
        return part;
      }
    }
    // Fallback to second part if no clear city found
    return parts[1];
  }
  
  return '';
};

// Function to determine project color based on new rules (same as in DashboardList)
const getProjectColor = (project: any): string => {
  if (!project) return 'bg-gray-500';
  
  // Check if project has no Action Items or Project Plan Items
  const hasNoItems = (!project.items || project.items.length === 0) && 
                     (!project.actionItems || project.actionItems.length === 0);
  
  if (hasNoItems) {
    return 'bg-green-500'; // Green if no items
  }
  
  // Check if all tasks have no risk or low risk
  const allItemsLowRisk = project.items?.every((item: any) => 
    !item.risks || item.risks === null || item.risks.toLowerCase() === 'low' || item.risks.toLowerCase() === 'none'
  ) ?? true;
  
  const allActionItemsLowRisk = project.actionItems?.every((item: any) => 
    !item.priority || item.priority === null || item.priority.toLowerCase() === 'low'
  ) ?? true;
  
  if (allItemsLowRisk && allActionItemsLowRisk) {
    return 'bg-green-500'; // Green if all risks are low or none
  }
  
  // Fall back to original status-based logic
  if (project.status === 'NOT_STARTED') return 'bg-gray-500';
  if (project.status === 'COMPLETED') return 'bg-orange-400';
  if (project.status === 'IN_PROGRESS') return 'bg-red-500';
  
  return 'bg-gray-500'; // Default
};

export const Room = () => {
  const params = useParams<{ projectId: string, siteId: string, floorId: string, roomId: string }>();
  const { keycloak: { token } } = useKeycloak();
  const navigate = useNavigate();
  const [showNewRowForm, setShowNewRowForm] = React.useState(false);
  const [showNewRackForm, setShowNewRackForm] = React.useState('');
  const [localRoom, setLocalRoom] = React.useState<RoomInterface | null>(null);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [isEditingRoomInfo, setIsEditingRoomInfo] = React.useState(false);
  const [editingRoomData, setEditingRoomData] = React.useState<Partial<RoomInterface>>({});
  const [roomTypes, setRoomTypes] = React.useState<RoomTypeInterface[]>([]);
  const [showNewProject, setShowNewProject] = React.useState(false);
  const [showAddTeamMember, setShowAddTeamMember] = React.useState(false);
  const [showAddTask, setShowAddTask] = React.useState(false);
  const [showAddActionItem, setShowAddActionItem] = React.useState(false);
  const [showAddDailyUpdate, setShowAddDailyUpdate] = React.useState(false);
  const [show3DView, setShow3DView] = React.useState(false);
  const { isFeatureEnabled } = useFeatureFlag();
  const { loadProjectById, loadProjectsPage } = useProjectsPage();
  const { loadFloors, loadRooms } = useLocationsPage();
  const { floors, roomsByFloor } = useLocationsStore();
  const { loadEscalationMatrixLevels } = useEscalationMatrixPage(params.projectId || '');
  const projectData: ProjectInterface | 'not-found' = useProjectsStore((state) => state.fullProjects.find((p) => p.id === params.projectId) || 'not-found');
  const floor = floors[params.siteId || '']?.find((f:FloorInterface) => f.id === params.floorId);
  const storeRoom = roomsByFloor[params.floorId || '']?.find((r:RoomInterface) => r.id === params.roomId);
  const room = localRoom || storeRoom;
  const siteId = params.siteId || (projectData !== 'not-found' ? (projectData?.sites?.[0]?.site_id || '') : '');
  const projectId = params.projectId;

  const [savingCabNumber, setSavingCabNumber] = React.useState<{[rackId: string]: boolean}>({});
  const [savingRowName, setSavingRowName] = React.useState<{[rowId: string]: boolean}>({});

  // Project users hook
  const { users: projectUsers, roles, addUser } = useProjectUsers(projectId || '');

  // Reset local room when roomId changes
  useEffect(() => {
    setLocalRoom(null);
  }, [params.roomId]);

  // Update local room when store room changes
  useEffect(() => {
    if (storeRoom && !localRoom) {
      setLocalRoom(storeRoom);
    }
  }, [storeRoom, localRoom]);

  // Auto-enable edit mode if room has no rows (racks)
  useEffect(() => {
    if (room && room.rows && room.rows.length === 0) {
      setIsEditMode(true);
    }
  }, [room]);

  // Debug console logs
  console.log('=== ROOM DEBUG ===');
  console.log('params:', params);
  console.log('projectData:', projectData);
  console.log('floors:', floors);
  console.log('roomsByFloor:', roomsByFloor);
  console.log('floor:', floor);
  console.log('storeRoom:', storeRoom);
  console.log('localRoom:', localRoom);
  console.log('room:', room);
  console.log('room?.rows:', room?.rows);
  console.log('siteId:', siteId);
  console.log('==================');

  useEffect(() => {
    if (token && projectData === 'not-found') {
      const loadProjectData = async () => {
        try {
          console.log('🏠 Loading project data for Room page...');
          await loadProjectsPage(1);
          await loadProjectById(params.projectId || '');
        } catch(e: any) {
          console.error('❌ Failed to load project data:', e);
          
          if (e.message?.includes('502')) {
            toast.error('Server temporarily unavailable. Please refresh the page in a few moments.');
          } else if (e.message?.includes('401')) {
            toast.error('Session expired. Please log in again.');
          } else {
            toast.error('Failed to load project data. Please try again.');
          }
        }
      };
      
      loadProjectData();
    }
  }, [params.projectId, token]);

  useEffect(() => {
    const load = async () => {
      if (!token || !params.projectId) {
        console.log('🏠 Skipping Room data load - no token or projectId');
        return;
      }

      try {
        console.log('🏠 Loading Room page data...');
        
        // Load floors if needed
        if (projectData !== 'not-found' && !floor) {
          console.log('🏠 Loading floors...');
          await loadFloors(params.projectId, siteId);
        }

        // Load rooms if needed
        if (!storeRoom) {
          console.log('🏠 Loading rooms...');
          await loadRooms(params.projectId, params.siteId || '', params.floorId || '');
        }
          
        // Load room types
        if (roomTypes.length === 0) {
          try {
            console.log('🏠 Loading room types...');
            const types = await getRoomTypes(token);
            console.log('✅ Room types loaded:', types?.length || 0);
            setRoomTypes(Array.isArray(types) ? types : []);
          } catch (error: any) {
            console.error('❌ Failed to load room types:', error);
            
            if (error.message?.includes('502')) {
              toast.error('Server temporarily unavailable when loading room types.');
            } else if (error.message?.includes('401')) {
              console.log('🔄 Token may be expired, will be handled by PrivateRoute');
            } else {
              console.warn('⚠️ Could not load room types, using empty list');
            }
            setRoomTypes([]);
          }
        } else {
          console.log('✅ Room types already loaded:', roomTypes.length);
        }
        
      } catch (error: any) {
        console.error('❌ Failed to load Room page data:', error);
        
        if (error.message?.includes('502')) {
          toast.error('Server temporarily unavailable. Please refresh the page.');
        } else if (error.message?.includes('401')) {
          console.log('🔄 Authentication error, will be handled by PrivateRoute');
        } else {
          toast.error('Failed to load room data. Please try refreshing the page.');
        }
      }
    }

    load();
  }, [params.siteId, params.floorId, params.roomId, token, projectData, storeRoom, floor, roomTypes.length]);

  // Room editing functions
  const startEditingRoom = () => {
    setIsEditingRoomInfo(true);
    setEditingRoomData({
      name: room?.name || '',
      type: room?.type || '',
      room_identifier: room?.room_identifier || '',
      directions: room?.directions || '',
      access_requirements: room?.access_requirements || '',
      access_contacts_name: room?.access_contacts_name || '',
      access_contacts_surname: room?.access_contacts_surname || '',
      contact_phone: room?.contact_phone || '',
      contact_email: room?.contact_email || '',
      room_dimensions: room?.room_dimensions || '',
      power_circuit: room?.power_circuit || '',
      row_number: room?.row_number || '',
      cold_hot_aisle: room?.cold_hot_aisle || '',
      area: room?.area || 0,
      cabinet_name: room?.cabinet_name || '',
      cabinet_location: room?.cabinet_location || '',
      rack_type: room?.rack_type || ''
    });
  };

  const cancelEditingRoom = () => {
    setIsEditingRoomInfo(false);
    setEditingRoomData({});
  };

  const updateEditingData = (field: string, value: string | number) => {
    setEditingRoomData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveRoomChanges = async () => {
    if (!token || !room?.id || !projectId) return;
    
    try {
      console.log('🏠 saveRoomChanges started');
      console.log('  - token present:', !!token);
      console.log('  - room.id:', room?.id);
      console.log('  - projectId:', projectId);
      console.log('  - Original room data:', room);
      console.log('  - Editing room data:', editingRoomData);
      
      // Create complete room object with updated data
      // Convert empty strings to null for validation
      const updatedRoom: RoomInterface = {
        ...room,
        ...editingRoomData,
        // Handle empty strings - convert to null for fields that require validation
        contact_phone: editingRoomData.contact_phone?.trim() || undefined,
        contact_email: editingRoomData.contact_email?.trim() || undefined,
        access_contacts_name: editingRoomData.access_contacts_name?.trim() || undefined,
        access_contacts_surname: editingRoomData.access_contacts_surname?.trim() || undefined,
        directions: editingRoomData.directions?.trim() || undefined,
        access_requirements: editingRoomData.access_requirements?.trim() || undefined,
        room_identifier: editingRoomData.room_identifier?.trim() || undefined,
        power_circuit: editingRoomData.power_circuit?.trim() || undefined,
        row_number: editingRoomData.row_number?.trim() || undefined,
        cold_hot_aisle: editingRoomData.cold_hot_aisle?.trim() || undefined,
        room_dimensions: editingRoomData.room_dimensions?.trim() || undefined,
        cabinet_name: editingRoomData.cabinet_name?.trim() || undefined,
        cabinet_location: editingRoomData.cabinet_location?.trim() || undefined,
        rack_type: editingRoomData.rack_type?.trim() || undefined,
      };
      
      console.log('📦 Final room object to send:', JSON.stringify(updatedRoom, null, 2));
      
      console.log('🌐 Calling updateRoom API...');
      const result = await updateRoom(token, projectId, room.id, updatedRoom);
      
      console.log('✅ updateRoom API response:', result);
      
      // Update local room state with the actual response from API
      setLocalRoom(result);
      
      setIsEditingRoomInfo(false);
      setEditingRoomData({});
      toast.success('Room information updated successfully');
      
      console.log('🔄 Reloading room data...');
      // Reload room data to ensure sync
      await loadRooms(params.projectId, params.siteId || '', params.floorId || '');
      
      console.log('✨ Room update completed successfully');
    } catch (error) {
      console.error('❌ saveRoomChanges failed:', error);
      console.error('  - Error type:', typeof error);
      if (error instanceof Error) {
        console.error('  - Error message:', error.message);
        console.error('  - Error stack:', error.stack);
      } else {
        console.error('  - Error value:', error);
      }
      toast.error('Failed to update room information');
    }
  };

  const addNewRowHandler = async (count: number) => {
    try {
      console.log(`🔄 Creating ${count} rows...`);
      
      // Create multiple rows in parallel
      const rowPromises = [];
      for (let i = 0; i < count; i++) {
        const rowData = {
          name: `Row ${(localRoom?.rows.length || 0) + i + 1}`,
          site_id: siteId,
          room_id: params.roomId || '',
          project_id: params.projectId || '',
          max_racks: 8, // Default max_racks value
        };
        
        console.log(`  - Creating row ${i + 1}/${count}: ${rowData.name}`);
        rowPromises.push(createRow(token ?? '', params.projectId || '', siteId, params.roomId || '', rowData));
      }
      
      // Wait for all rows to be created
      const responses = await Promise.all(rowPromises);
      console.log(`✅ Successfully created ${responses.length} rows`);
      
      // Close modal immediately
      setShowNewRowForm(false);
      
      // Add new rows to local room state with proper typing
      if (localRoom && responses.length > 0) {
        const newRows: RowInterface[] = responses.map((response, index) => ({
          id: response.id || Date.now().toString() + index,
          site_id: siteId,
          room_id: params.roomId || '',
          project_id: params.projectId,
          name: `Row ${(localRoom?.rows.length || 0) + index + 1}`,
          max_racks: 8,
          racks: []
        }));
        
        setLocalRoom(prev => prev ? {
          ...prev,
          rows: [...prev.rows, ...newRows]
        } : prev);
      }
      
      // Success message
      toast.success(`${count} row${count > 1 ? 's' : ''} created successfully`);
    } catch(e) {
      console.error('❌ Failed to create rows:', e);
      toast.error('Failed to create rows');
    }
  }

  const addNewRackHandler = (rowId: string) => async (baseName: string, total_units: number, max_power: number, count: number) => {
    try {
      const createdRacks: RackInterface[] = [];
      
      // Create racks sequentially
      for (let i = 1; i <= count; i++) {
        const rackNumber = count === 1 ? baseName : `${baseName} ${i}`;
        
        const response = await createRack(token ?? '', params.projectId || '', siteId,  rowId, {
          site_id: siteId,
          row_id: rowId,
          project_id: params.projectId || '',
          number: rackNumber,
          total_units,
          max_power,
        });
        
        if (response) {
          const newRack: RackInterface = {
            id: response.id || Date.now().toString(),
            site_id: siteId,
            row_id: rowId,
            project_id: params.projectId,
            number: rackNumber,
            total_units,
            max_power
          };
          
          createdRacks.push(newRack);
        }
      }
      
      // Close modal immediately
      setShowNewRackForm('');
      
      // Add all new racks to local room state
      if (localRoom && createdRacks.length > 0) {
        setLocalRoom(prev => {
          if (!prev) return prev;
          
          const updatedRows = prev.rows.map(row => {
            if (row.id === rowId) {
              return {
                ...row,
                racks: [...(row.racks || []), ...createdRacks]
              };
            }
            return row;
          });
          
          return {
            ...prev,
            rows: updatedRows
          };
        });
      }
      
      // Success message
      toast.success(`${count} rack${count > 1 ? 's' : ''} created successfully`);
    } catch(e) {
      toast.error('Failed to create racks');
    }
  }

  const handleAddTeamMember = async (_userId: string, userData: any) => {
    try {
      if (!projectId) return;
      
      await addUser('', userData);
      toast.success('Team member added successfully');
      setShowAddTeamMember(false);
      
              // Navigate to project page and switch to Team tab
      localStorage.setItem('activeProjectTab', 'team');
      navigate(`/projects/${projectId}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add team member';
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleAddTask = async (taskData: ProjectItemInterface) => {
    try {
      if (!token || !projectId) return;
      
      await createProjectItem(token, projectId, taskData);
      
      toast.success('Task created successfully');
      setShowAddTask(false);
      
      // Reload project data
      loadProjectById(projectId);
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleAddActionItem = async (actionItemData: ProjectActionItem) => {
    try {
      if (!token || !projectId) return;
      
      await createProjectActionItems(token, projectId, actionItemData);
      
      toast.success('Action item created successfully');
      setShowAddActionItem(false);
      
      // Reload project data
      loadProjectById(projectId);
    } catch (error) {
      toast.error('Failed to create action item');
    }
  };

  const handleAddDailyUpdate = async (dailyUpdateData: ProjectDailyUpdate) => {
    try {
      if (!token || !projectId) return;
      
      await createProjectDailyUpdates(token, projectId, dailyUpdateData);
      
      toast.success('Daily update created successfully');
      setShowAddDailyUpdate(false);
      
      // Reload project data
      loadProjectById(projectId);
    } catch (error) {
      toast.error('Failed to create daily update');
    }
  };

  if (projectData === 'not-found') {
    return (
      <div className="container p-4 mx-auto min-h-screen flex items-center justify-center">
        <LoadingAnimation 
          size={150} 
          text="Loading project data..." 
          className="text-center"
        />
      </div>
    );
  }

  // Map viewport configuration - use project site coordinates or fallback to default
  const getMapCoordinates = () => {
    // Try to get coordinates from project site data
    if (typeof projectData === 'object' && projectData?.sites && projectData.sites.length > 0) {
      const defaultSite = projectData.sites.find((site: any) => site.is_default) || projectData.sites[0];
      if (defaultSite?.geo_location) {
        const [lat, lng] = defaultSite.geo_location;
      
        const siteName = defaultSite.name || 'Unknown Site';
        const siteAddress = defaultSite.address || 'No address';
        console.log('📍 Using project site coordinates:', { 
          lat, 
          lng, 
          siteName, 
          siteAddress,
          projectName: projectData.name 
        });
        return { latitude: lat, longitude: lng };
      }
    }
    
    // Fallback to default coordinates (Silicon Valley)
    console.log('📍 Using fallback coordinates (no project site data)');
    console.log('  - projectData type:', typeof projectData);
    console.log('  - projectData sites:', typeof projectData === 'object' ? projectData?.sites : 'N/A');
    return { latitude: 37.3382, longitude: -122.0922 };
  };

  const mapCoordinates = getMapCoordinates();
  
  const VIEWPORT = {
    latitude: mapCoordinates.latitude,
    longitude: mapCoordinates.longitude,
    zoom: 14,
    maxZoom: 18,
    minZoom: 10,
  };

  // Calculate room statistics
  const calculateRoomStats = () => {
    if (!room?.rows) return { totalRacks: 0, totalUnits: 0, occupiedUnits: 0, freeUnits: 0 };
    
    let totalRacks = 0;
    let totalUnits = 0;
    let occupiedUnits = 0;
    
    room.rows.forEach(row => {
      if (row.racks) {
        totalRacks += row.racks.length;
        row.racks.forEach(rack => {
          totalUnits += rack.total_units || 0;
          // TODO: Add logic to calculate occupied units based on actual devices
          // For now, we'll use a placeholder calculation
          occupiedUnits += 0; // This should be calculated based on actual devices in the rack
        });
      }
    });
    
    const freeUnits = totalUnits - occupiedUnits;
    
    return { totalRacks, totalUnits, occupiedUnits, freeUnits };
  };

  const roomStats = calculateRoomStats();

  return (
    <div className="container p-4 mx-auto">
      {showNewProject && (
        <NewProjectForm 
          onClose={() => setShowNewProject(false)} 
          onProjectCreated={async (projectId: string) => {
            // Set active tab to team for the newly created project
            localStorage.setItem('activeProjectTab', 'team');
            // Navigate to the new project's Team tab
            navigate(`/projects/${projectId}`);
          }}
        />
      )}
      {showAddTeamMember && projectId && typeof projectData === 'object' && (
        <AddUserModal
          isOpen={showAddTeamMember}
          onClose={() => setShowAddTeamMember(false)}
          onAddUser={handleAddTeamMember}
          availableUsers={[]}
          projectUsers={(projectUsers || []).map((u: any) => u.user)}
          roles={roles || []}
          projectId={projectId}
          onEscalationLevelsUpdate={loadEscalationMatrixLevels}
        />
      )}
      {showAddTask && projectId && typeof projectData === 'object' && (
        <AddProjectItemModal
          isOpen={showAddTask}
          onClose={() => setShowAddTask(false)}
          onAddItem={handleAddTask}
          projectData={projectData}
          users={(projectUsers || []).map(u => u.user)}
        />
      )}
      {showAddActionItem && projectId && typeof projectData === 'object' && (
        <AddActionItemModal
          isOpen={showAddActionItem}
          onClose={() => setShowAddActionItem(false)}
          onAddItem={handleAddActionItem}
          projectData={projectData}
          users={(projectUsers || []).map(u => u.user)}
        />
      )}
      {showAddDailyUpdate && projectId && typeof projectData === 'object' && (
        <AddDailyUpdateModal
          isOpen={showAddDailyUpdate}
          onClose={() => setShowAddDailyUpdate(false)}
          onAddItem={handleAddDailyUpdate}
          projectData={projectData}
        />
      )}
      <div className="flex items-center justify-between mb-10">
        <div className="flex gap-2 items-center">
          <Link 
            to={`/projects/${params.projectId}`} 
            style={{
              color: DesignSystem.colors.link.primary,
              fontFamily: DesignSystem.typography.body.fontFamily,
              fontSize: DesignSystem.typography.body.fontSize,
              textDecoration: 'none',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = DesignSystem.colors.link.hover}
            onMouseLeave={(e) => e.currentTarget.style.color = DesignSystem.colors.link.primary}
          >
            {typeof projectData === 'object' && projectData?.name || 'Project'}
          </Link>
          <span style={{ 
            color: DesignSystem.colors.gray[400],
            fontFamily: DesignSystem.typography.body.fontFamily,
            fontSize: DesignSystem.typography.body.fontSize
          }}>/</span>
          <Link 
            to={`/projects/${params.projectId}/sites/${params.siteId}`}
            style={{
              color: DesignSystem.colors.link.primary,
              fontFamily: DesignSystem.typography.body.fontFamily,
              fontSize: DesignSystem.typography.body.fontSize,
              textDecoration: 'none',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = DesignSystem.colors.link.hover}
            onMouseLeave={(e) => e.currentTarget.style.color = DesignSystem.colors.link.primary}
          >
            {typeof projectData === 'object' && projectData.sites ? 
              projectData.sites.find((site: any) => site.id === params.siteId || site.site_id === params.siteId)?.name || 'Site'
              : 'Site'
            }
          </Link>
          <span style={{ 
            color: DesignSystem.colors.gray[400],
            fontFamily: DesignSystem.typography.body.fontFamily,
            fontSize: DesignSystem.typography.body.fontSize
          }}>/</span>
          <Link 
            to={`/projects/${params.projectId}/sites/${params.siteId}/floors/${params.floorId}`}
            style={{
              color: DesignSystem.colors.link.primary,
              fontFamily: DesignSystem.typography.body.fontFamily,
              fontSize: DesignSystem.typography.body.fontSize,
              textDecoration: 'none',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = DesignSystem.colors.link.hover}
            onMouseLeave={(e) => e.currentTarget.style.color = DesignSystem.colors.link.primary}
          >
            {floor?.name || 'Floor'}
          </Link>
          <span style={{ 
            color: DesignSystem.colors.gray[400],
            fontFamily: DesignSystem.typography.body.fontFamily,
            fontSize: DesignSystem.typography.body.fontSize
          }}>/</span>
          <span style={{
            color: DesignSystem.colors.primary.text,
            fontFamily: DesignSystem.typography.body.fontFamily,
            fontSize: DesignSystem.typography.body.fontSize,
            fontWeight: 500
          }}>
            {room?.name || 'Room'}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <CreateDropdown 
            onSelectProject={() => setShowNewProject(true)}
            onSelectTeamMember={() => setShowAddTeamMember(true)}
            onSelectTask={() => setShowAddTask(true)}
            onSelectActionItem={() => setShowAddActionItem(true)}
            onSelectDailyUpdate={() => setShowAddDailyUpdate(true)}
          />
          <NotificationButton />
          <UserInfo />
        </div>
      </div>
      <div className="p-2">


        {/* Room Overview - Compact unified design */}
        <div className="mb-6">
          {/* Main Header Card - Restored */}
          <div className="bg-white rounded-lg transition-shadow duration-200 shadow-sm mb-6" style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
            {/* Header Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
              <div className="flex items-center justify-between">
                {/* Left side - Room Info */}
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-3">
                    <FaServer className="text-blue-600 w-5 h-5" />
                    <h2 className="text-xl font-semibold" style={{ fontFamily: 'Arcadia Display, system-ui, sans-serif' }}>
                      {room?.name || 'Room'} Overview
                    </h2>
                  </div>
                  
                  {/* Quick Info Badges */}
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-md text-xs font-medium ${
                      room?.type === 'Server' ? 'bg-green-100 text-green-800' :
                      room?.type === 'Network' ? 'bg-blue-100 text-blue-800' :
                      room?.type === 'Storage' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`} style={{ fontFamily: 'Arcadia Text, system-ui, sans-serif' }}>
                      {room?.type || 'Unknown Type'}
                    </span>
                    <span className="px-3 py-1 rounded-md text-xs font-medium bg-emerald-100 text-emerald-800" style={{ fontFamily: 'Arcadia Text, system-ui, sans-serif' }}>
                      {room?.rows?.length || 0} Rows
                    </span>
                    <span className="px-3 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800" style={{ fontFamily: 'Arcadia Text, system-ui, sans-serif' }}>
                      {roomStats.totalRacks} Racks
                    </span>
                    {roomStats.occupiedUnits > 0 ? (
                      <span className="px-3 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-800" style={{ fontFamily: 'Arcadia Text, system-ui, sans-serif' }}>
                        {roomStats.occupiedUnits}/{roomStats.totalUnits} Units
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800" style={{ fontFamily: 'Arcadia Text, system-ui, sans-serif' }}>
                        {roomStats.totalUnits} Units Available
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Right side - Actions */}
                <div className="flex items-center space-x-3">
                  {!isEditingRoomInfo && (
                    <>
                      {isFeatureEnabled('ROOM_3D_VIEW') && (
                        <button
                          onClick={() => setShow3DView(true)}
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-md transition-colors font-medium"
                          style={{ fontFamily: 'Arcadia Text, system-ui, sans-serif' }}
                        >
                          <FaCube className="w-4 h-4" />
                          <span>3D View</span>
                        </button>
                      )}
                      <button
                        onClick={startEditingRoom}
                        className="flex items-center space-x-2 px-4 py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors font-medium"
                        style={{ fontFamily: 'Arcadia Text, system-ui, sans-serif' }}
                      >
                        <FiEdit3 className="w-4 h-4" />
                        <span>Edit Details</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {/* Stats Row */}
            <div className="px-6 py-2">
              <div className="flex items-center text-xs font-medium" style={{ color: DesignSystem.colors.primary.textSecondary, fontFamily: 'Arcadia Text, system-ui, sans-serif' }}>
                <div className="flex-1">
                  Comprehensive room information and technical specifications
                </div>
                <div className="flex items-center space-x-4">
                  <span>Floor: {floor?.name || 'N/A'}</span>
                  <span>•</span>
                  <span>Area: {room?.area ? `${room.area} sq ft` : 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* 4 Neat Cards Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Project & Location Card */}
            <div className="bg-white rounded-lg shadow-sm p-4" style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
              <div className="flex items-center space-x-2 mb-3">
                <FiMapPin className="w-4 h-4 text-blue-600" />
                <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Arcadia Display, system-ui, sans-serif' }}>
                  Project & Location <span className={getBadgeTailwindClasses('needReview')}>Need Review</span>
                </h3>
              </div>
              <div className="space-y-2 text-xs" style={{ fontFamily: 'Arcadia Text, system-ui, sans-serif' }}>
                <div className="flex justify-between">
                  <span className="text-gray-600">Project:</span>
                  <span className="font-medium text-right">{typeof projectData === 'object' ? projectData.name : 'Unknown'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Site:</span>
                  <span className="font-medium text-right">
                    {typeof projectData === 'object' && projectData.sites ? 
                      projectData.sites.find((site: any) => site.id === siteId || site.site_id === siteId)?.name || 'N/A' 
                      : 'N/A'
                    }
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Floor:</span>
                  <span className="font-medium text-right">{floor?.name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Room Name:</span>
                  {isEditingRoomInfo ? (
                    <input
                      type="text"
                      value={editingRoomData.name || ''}
                      onChange={(e) => updateEditingData('name', e.target.value)}
                      className="font-medium bg-gray-50 border border-gray-200 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 w-16"
                    />
                  ) : (
                    <span className="font-medium text-right">{room?.name || 'N/A'}</span>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Room ID:</span>
                  {isEditingRoomInfo ? (
                    <input
                      type="text"
                      value={editingRoomData.room_identifier || ''}
                      onChange={(e) => updateEditingData('room_identifier', e.target.value)}
                      className="font-medium bg-gray-50 border border-gray-200 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 w-16"
                    />
                  ) : (
                    <span className="font-medium text-right">{room?.room_identifier || 'N/A'}</span>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Room Type:</span>
                  {isEditingRoomInfo ? (
                    <select
                      value={editingRoomData.type || ''}
                      onChange={(e) => updateEditingData('type', e.target.value)}
                      className="font-medium bg-gray-50 border border-gray-200 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 w-20"
                    >
                      <option value="">Select Type</option>
                      {roomTypes.map((type) => (
                        <option key={type.id} value={type.name}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="font-medium text-right">{room?.type || 'N/A'}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Technical Specs Card */}
            <div className="bg-white rounded-lg shadow-sm p-4" style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
              <div className="flex items-center space-x-2 mb-3">
                <FiSettings className="w-4 h-4 text-blue-600" />
                <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Arcadia Display, system-ui, sans-serif' }}>
                  Technical Specs <span className={getBadgeTailwindClasses('needReview')}>Need Review</span>
                </h3>
              </div>
              <div className="space-y-2 text-xs" style={{ fontFamily: 'Arcadia Text, system-ui, sans-serif' }}>
                <div className="flex justify-between">
                  <span className="text-gray-600">Area:</span>
                  {isEditingRoomInfo ? (
                    <input
                      type="number"
                      value={editingRoomData.area || ''}
                      onChange={(e) => updateEditingData('area', parseFloat(e.target.value) || 0)}
                      className="font-medium bg-gray-50 border border-gray-200 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 w-12"
                    />
                  ) : (
                    <span className="font-medium text-right">{room?.area ? `${room.area} sq ft` : 'N/A'}</span>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Dimensions:</span>
                  {isEditingRoomInfo ? (
                    <input
                      type="text"
                      value={editingRoomData.room_dimensions || ''}
                      onChange={(e) => updateEditingData('room_dimensions', e.target.value)}
                      className="font-medium bg-gray-50 border border-gray-200 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 w-16"
                    />
                  ) : (
                    <span className="font-medium text-right">{room?.room_dimensions || 'N/A'}</span>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Rack Type:</span>
                  {isEditingRoomInfo ? (
                    <input
                      type="text"
                      value={editingRoomData.rack_type || ''}
                      onChange={(e) => updateEditingData('rack_type', e.target.value)}
                      className="font-medium bg-gray-50 border border-gray-200 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 w-16"
                    />
                  ) : (
                    <span className="font-medium text-right">{room?.rack_type || 'N/A'}</span>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Power Circuit:</span>
                  {isEditingRoomInfo ? (
                    <input
                      type="text"
                      value={editingRoomData.power_circuit || ''}
                      onChange={(e) => updateEditingData('power_circuit', e.target.value)}
                      className="font-medium bg-gray-50 border border-gray-200 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 w-16"
                    />
                  ) : (
                    <span className="font-medium text-right">{room?.power_circuit || 'N/A'}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Access & Contact Card */}
            <div className="bg-white rounded-lg shadow-sm p-4" style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
              <div className="flex items-center space-x-2 mb-3">
                <FiUser className="w-4 h-4 text-blue-600" />
                <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Arcadia Display, system-ui, sans-serif' }}>
                  Access & Contact
                </h3>
              </div>
              <div className="space-y-2 text-xs" style={{ fontFamily: 'Arcadia Text, system-ui, sans-serif' }}>
                <div className="flex justify-between">
                  <span className="text-gray-600">Contact:</span>
                  {isEditingRoomInfo ? (
                    <input
                      type="text"
                      value={`${editingRoomData.access_contacts_name || ''} ${editingRoomData.access_contacts_surname || ''}`.trim()}
                      onChange={(e) => {
                        const parts = e.target.value.split(' ');
                        updateEditingData('access_contacts_name', parts[0] || '');
                        updateEditingData('access_contacts_surname', parts.slice(1).join(' ') || '');
                      }}
                      className="font-medium bg-gray-50 border border-gray-200 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 w-20"
                    />
                  ) : (
                    <span className="font-medium text-right">
                      {`${room?.access_contacts_name || ''} ${room?.access_contacts_surname || ''}`.trim() || 'N/A'}
                    </span>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone:</span>
                  {isEditingRoomInfo ? (
                    <input
                      type="text"
                      value={editingRoomData.contact_phone || ''}
                      onChange={(e) => updateEditingData('contact_phone', e.target.value)}
                      className="font-medium bg-gray-50 border border-gray-200 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 w-20"
                    />
                  ) : (
                    <span className="font-medium text-right">{room?.contact_phone || 'N/A'}</span>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  {isEditingRoomInfo ? (
                    <input
                      type="email"
                      value={editingRoomData.contact_email || ''}
                      onChange={(e) => updateEditingData('contact_email', e.target.value)}
                      className="font-medium bg-gray-50 border border-gray-200 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 w-20"
                    />
                  ) : (
                    <span className="font-medium text-right">{room?.contact_email || 'N/A'}</span>
                  )}
                </div>
                {(room?.access_requirements || isEditingRoomInfo) && (
                  <div className="mt-2">
                    <span className="text-gray-600 text-xs">Access Requirements:</span>
                    {isEditingRoomInfo ? (
                      <textarea
                        value={editingRoomData.access_requirements || ''}
                        onChange={(e) => updateEditingData('access_requirements', e.target.value)}
                        className="w-full mt-1 bg-gray-50 border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                        rows={2}
                        placeholder="Enter access requirements..."
                      />
                    ) : (
                      <div className="text-xs text-gray-700 mt-1">{room?.access_requirements}</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Location Map Card */}
            <div className="bg-white rounded-lg shadow-sm border border-[#e5e7eb] overflow-hidden sticky top-4">
              <div className="p-3 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Arcadia Display, system-ui, sans-serif' }}>
                  Location
                </h3>
              </div>
              <div className="h-48 relative">
                <link href='https://api.tiles.mapbox.com/mapbox-gl-js/v3.10.0/mapbox-gl.css' rel='stylesheet' />
                <Map
                  initialViewState={VIEWPORT}
                  style={{width: '100%', height: '100%'}}
                  mapboxAccessToken='pk.eyJ1IjoiZWlnaHR5ZGF5cyIsImEiOiJjam9zYXBiZGswb2NtM2pwdHY5eGZmcGR5In0.lJpch0oIthk_eye7jJnq5A'
                  mapStyle="mapbox://styles/eightydays/cjosb2zt801sy2ro3pozm9nn6"
                >
                  <Marker 
                    latitude={mapCoordinates.latitude} 
                    longitude={mapCoordinates.longitude} 
                    anchor="bottom"
                  >
                    <div className={cn(
                      "marker bg-white rounded-full shadow-md flex justify-start items-center p-1 gap-1 hover:bg-[#F0F1F4] transition-all border border-[rgba(112,115,147,0.16)]",
                    )}>
                      <div className={cn('w-6 h-6 rounded-full text-white flex justify-center items-center text-xs', getProjectColor(projectData))}>
                        {typeof projectData === 'object' && projectData?.status === 'NOT_STARTED' && <HiOutlineBadgeCheck size={14} />}
                        {typeof projectData === 'object' && projectData?.status === 'COMPLETED' && <BsClock size={14} />}
                        {typeof projectData === 'object' && projectData?.status === 'IN_PROGRESS' && (projectData?.items?.length || '')}
                      </div>
                      <div className='leading-tight pr-2'>
                        <div className='text-xs font-medium font-["Arcadia_Text",system-ui,sans-serif]'>
                          {typeof projectData === 'object' ? projectData?.name : 'Project'}
                        </div>
                        <div className='text-xs text-gray-500 font-["Arcadia_Text",system-ui,sans-serif]'>
                          {typeof projectData === 'object' ? getCityFromSite(projectData?.sites?.[0]) : 'Location'}
                        </div>
                      </div>
                    </div>
                  </Marker>
                </Map>
              </div>
              <div className="p-3 border-t border-gray-200">
                <div className="text-xs text-gray-600">
                  <div className="font-medium mb-1">{room?.name || 'Room'}</div>
                  <div className="text-xs">
                    {typeof projectData === 'object' && projectData?.sites && projectData.sites.length > 0 
                      ? (projectData.sites.find((site: any) => site.is_default) || projectData.sites[0])?.address || 'No address available'
                      : 'No address available'
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Edit Action Buttons */}
          {isEditingRoomInfo && (
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={cancelEditingRoom}
                className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                <FiX className="w-3 h-3" />
                <span>Cancel</span>
              </button>
              <button
                onClick={saveRoomChanges}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                <FiSave className="w-3 h-3" />
                <span>Save</span>
              </button>
            </div>
          )}
        </div>

        {/* Add rows button - only show in edit mode */}
        {isEditMode && (
        <div className="mb-6">
          <button
            className="cursor-pointer bg-white hover:bg-blue-50 border border-gray-100 text-blue-500 font-bold py-2 px-4 rounded-full"
              style={{ fontFamily: 'Arcadia Text, system-ui, sans-serif' }}
            onClick={() => setShowNewRowForm(true)}
          >
            Add rows
          </button>
        </div>
        )}

        <div className="flex items-center justify-between my-10">
          <div className="flex items-center space-x-6">
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Arcadia Display, system-ui, sans-serif' }}>Top Down View of Room</h2>
            {/* Units legend */}
            <div className="flex items-center space-x-4 text-xs">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span className="text-gray-600">Total</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-gray-600">Free</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                <span className="text-gray-600">Occupied</span>
              </div>
            </div>
          </div>
          <button
            onClick={() => setIsEditMode(!isEditMode)}
            className={`flex items-center space-x-2 px-4 py-2 text-sm rounded-md transition-colors font-medium ${
              isEditMode 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
            }`}
            style={{ fontFamily: 'Arcadia Text, system-ui, sans-serif' }}
          >
            <FiEdit3 className="w-4 h-4" />
            <span>{isEditMode ? 'Save' : 'Edit'}</span>
          </button>
        </div>

        <table className="w-full" style={{ fontFamily: 'Circe, system-ui, sans-serif' }}>
          <tbody>
          {room?.rows.map((row, rowIndex) => (
            <React.Fragment key={row.id}>
              <tr className="flex font-bold border-b-1 border-dotted">
                <td className="p-2" style={{ width: '11%' }}>
                  Hot Aisle
                </td>
                {row.racks?.map((rack) => (
                  <td key={rack.id} className="p-2" style={{ width: '11%' }}>
                    {rack.number}
                  </td>
                ))}
              </tr>
              <tr className="flex text-bold">
                <td className="p-2 flex items-center" style={{ width: '11%' }}>
                  {isEditMode ? (
                    <div className="relative w-full">
                      <input
                        type="text"
                        defaultValue={row.name || `Row ${rowIndex + 1}`}
                        className="w-full text-center bg-transparent border border-transparent rounded px-1 py-0.5 hover:border-gray-200 focus:border-blue-300 focus:bg-white focus:outline-none text-sm font-bold"
                        style={{ fontFamily: 'Circe, system-ui, sans-serif' }}
                        disabled={!!savingRowName[row.id || '']}
                        onBlur={async (e) => {
                          const newValue = e.target.value;
                          if (newValue === row.name) return;
                          if (!row.id || !token) return;
                          setSavingRowName(prev => ({ ...prev, [row.id as string]: true }));
                          try {
                            await updateRow(token, row.id, { ...row, name: newValue });
                            setLocalRoom(prev => {
                              if (!prev) return prev;
                              return {
                                ...prev,
                                rows: prev.rows.map(rw =>
                                  rw.id === row.id ? { ...rw, name: newValue } : rw
                                )
                              };
                            });
                            toast.success('Row name updated successfully');
                          } catch {
                            toast.error('Failed to update row name');
                          } finally {
                            setSavingRowName(prev => ({ ...prev, [row.id as string]: false }));
                          }
                        }}
                        placeholder={`Row ${rowIndex + 1}`}
                      />
                      {savingRowName[row.id || ''] && (
                        <span className="absolute right-1 top-1 text-xs text-gray-400">...</span>
                      )}
                    </div>
                  ) : (
                    <span>{row.name || `Row ${rowIndex + 1}`}</span>
                  )}
                </td>
                {row.racks?.map((rack, index) => {
                  const isFirst = index === 0;
                  const isLast = index === (row.racks?.length || 0) - 1;
                  const roundedClass = isFirst ? 'rounded-l-lg' : isLast ? 'rounded-r-lg' : '';
                  const borderClass = isLast ? 'border border-[#e5e7eb]' : 'border-t border-b border-l border-[#e5e7eb]';
                  
                  return (
                  <td key={rack.id} style={{ width: '11%' }}>
                    <div className={'h-8 text-center p-2'}>{rack.max_power}</div>
                      <div className={`h-30 flex justify-center items-center ${borderClass} ${roundedClass} overflow-hidden`}>
                      <Link
                        className="h-[100%] flex flex-col justify-center items-center hover:bg-gray-100 w-[100%] p-2"
                        to={`/projects/${params.projectId}/sites/${params.siteId}/floors/${params.floorId}/rooms/${params.roomId}/rows/${row.id}/racks/${rack.id}`}
                      >
                        <div className="text-sm font-medium text-gray-900">
                          {rack.total_units} Units
                        </div>
                        {/* Units statistics */}
                        <div className="flex items-center justify-center space-x-2 mt-1">
                          <div className="flex items-center space-x-1">
                            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                            <span className="text-xs text-blue-600">{rack.total_units}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                            <span className="text-xs text-green-600">{Math.max(0, rack.total_units - (rack.units?.length || 0))}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                            <span className="text-xs text-amber-600">{rack.units?.length || 0}</span>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </td>
                  );
                })}
                {/* Add New Rack button - only show in edit mode */}
                {isEditMode && (
                <td style={{ width: '11%' }}>
                  <button
                      className="cursor-pointer bg-white hover:bg-blue-50 text-blue-500 font-bold py-2 px-4 h-30 mt-8 rounded-md"
                      style={{ fontFamily: 'Arcadia Text, system-ui, sans-serif' }}
                    onClick={() => setShowNewRackForm(row.id || '')}
                  >
                    Add New Rack
                  </button>
                </td>
                )}
              </tr>
              <tr className="flex text-bold mb-10">
                <td className="p-2" style={{ width: '11%' }}>
                  Cab Number
                </td>
                {row.racks?.map((rack, index) => (
                  <td key={rack.id} className={`p-2`} style={{ width: '11%' }}>
                    {isEditMode ? (
                      rack.id ? (
                        <div className="relative">
                          <input
                            type="text"
                            defaultValue={rack.cab_number || (index + 1).toString()}
                            className="w-full text-center bg-transparent border border-transparent rounded px-1 py-0.5 hover:border-gray-200 focus:border-blue-300 focus:bg-white focus:outline-none text-sm"
                            style={{ fontFamily: 'Circe, system-ui, sans-serif' }}
                            disabled={!!savingCabNumber[rack.id]}
                            onBlur={async (e) => {
                              const newValue = e.target.value;
                              if (newValue === rack.cab_number) return;
                              if (!rack.id || !token) return;
                              setSavingCabNumber(prev => ({ ...prev, [rack.id as string]: true }));
                              try {
                                await updateRack(token, rack.id, { ...rack, cab_number: newValue });
                                setLocalRoom(prev => {
                                  if (!prev) return prev;
                                  return {
                                    ...prev,
                                    rows: prev.rows.map(rw =>
                                      rw.id === row.id ? {
                                        ...rw,
                                        racks: rw.racks?.map(r => r.id === rack.id ? { ...r, cab_number: newValue } : r)
                                      } : rw
                                    )
                                  };
                                });
                              } catch {
                                toast.error('Failed to update cab number');
                              } finally {
                                setSavingCabNumber(prev => ({ ...prev, [rack.id as string]: false }));
                              }
                            }}
                            placeholder={(index + 1).toString()}
                          />
                          {savingCabNumber[rack.id] && (
                            <span className="absolute right-1 top-1 text-xs text-gray-400">...</span>
                          )}
                        </div>
                      ) : ''
                    ) : (
                      <span>{rack.cab_number || ''}</span>
                    )}
                  </td>
                ))}
              </tr>
            </React.Fragment>
          ))}
          </tbody>
        </table>
      </div>
      {showNewRowForm && (
        <NewRowForm
          onClose={() => setShowNewRowForm(false)}
          onCreate={addNewRowHandler}
        />
      )}
      {(showNewRackForm && showNewRackForm !== '') && (
        <NewRackForm
          onClose={() => setShowNewRackForm('')}
          onCreate={addNewRackHandler(showNewRackForm)}
        />
      )}
      
      {/* 3D View Modal */}
      {room && isFeatureEnabled('ROOM_3D_VIEW') && (
        <Room3DView
          room={room}
          projectId={params.projectId || ''}
          siteId={params.siteId || ''}
          floorId={params.floorId || ''}
          roomId={params.roomId || ''}
          isOpen={show3DView}
          onClose={() => setShow3DView(false)}
        />
      )}
    </div>
  );
};
